import { NgTemplateOutlet } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ProjectKeyValEntry, ProjectKeyValMaxAgeRule, isSystemKeyValType } from '@shared/ProjectKeyValStore';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { SelectModule } from 'primeng/select';
import { MenuModule } from 'primeng/menu';
import { DialogModule } from 'primeng/dialog';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { TooltipModule } from 'primeng/tooltip';
import { ConfirmationService, MenuItem } from 'primeng/api';
import { Menu } from 'primeng/menu';
import { Subscription } from 'rxjs';
import { ProjectKeyvalAdminService } from 'src/app/shared/services/project-keyval-admin.service';
import { ProjectService } from 'src/app/shared/services/project.service';
import { SocketAdminService } from '@shared-angular/services/socketadmin.service';
import { ProjectSubmenuComponent } from 'src/app/project-submenu/project-submenu.component';
import { XlsxService } from '@reflact/ngx-xlsx';
import { XLSXExportConfig } from 'src/app/shared/types/xlsx.types';
import { ToastrService } from '@shared-angular/services/toastr.service';

@Component({
  imports: [NgTemplateOutlet, FormsModule, InputTextModule, TableModule, ButtonModule, TagModule, SelectModule, MenuModule, ConfirmDialogModule, DialogModule, IconFieldModule, InputIconModule, TooltipModule, ProjectSubmenuComponent],
  providers: [ConfirmationService],
  templateUrl: './project-keyvals-tab.component.html'
})
export class ProjectKeyValsTabComponent implements OnInit, OnDestroy {
  @ViewChild('keyvalMenu') public keyvalMenu!: Menu;
  private readonly _keyvals = signal<ProjectKeyValEntry<string>[]>([]);
  public get keyvals(): ProjectKeyValEntry<string>[] { return this._keyvals(); }
  public set keyvals(value: ProjectKeyValEntry<string>[]) { this._keyvals.set(value); }
  private readonly _filteredKeyVals = signal<ProjectKeyValEntry<string>[]>([]);
  public get filteredKeyVals(): ProjectKeyValEntry<string>[] { return this._filteredKeyVals(); }
  public set filteredKeyVals(value: ProjectKeyValEntry<string>[]) { this._filteredKeyVals.set(value); }
  private readonly _maxAgeRules = signal<ProjectKeyValMaxAgeRule[]>([]);
  public get maxAgeRules(): ProjectKeyValMaxAgeRule[] { return this._maxAgeRules(); }
  public set maxAgeRules(value: ProjectKeyValMaxAgeRule[]) { this._maxAgeRules.set(value); }
  public filterString: string = "";
  public showSystemEntries: boolean = false;
  private readonly _userMap = signal<Record<string, { name: string; guestId?: string }>>({});
  public get userMap(): Record<string, { name: string; guestId?: string }> { return this._userMap(); }
  public set userMap(value: Record<string, { name: string; guestId?: string }>) { this._userMap.set(value); }

  public activeKeyvalMenuItems: MenuItem[] = [];
  private readonly _editingEntry = signal<ProjectKeyValEntry<string> | null>(null);
  public get editingEntry(): ProjectKeyValEntry<string> | null { return this._editingEntry(); }
  public set editingEntry(value: ProjectKeyValEntry<string> | null) { this._editingEntry.set(value); }
  private readonly _editValue = signal('');
  public get editValue(): string { return this._editValue(); }
  public set editValue(value: string) { this._editValue.set(value); }
  private readonly _showEditKeyValDialog = signal(false);
  public get showEditKeyValDialog(): boolean { return this._showEditKeyValDialog(); }
  public set showEditKeyValDialog(value: boolean) { this._showEditKeyValDialog.set(value); }

  private readonly _editingRule = signal<ProjectKeyValMaxAgeRule | null>(null);
  public get editingRule(): ProjectKeyValMaxAgeRule | null { return this._editingRule(); }
  public set editingRule(value: ProjectKeyValMaxAgeRule | null) { this._editingRule.set(value); }
  public editRuleKeyPattern: string = "";
  public editRuleMaxAgeSeconds: number = 0;
  private readonly _showEditRuleDialog = signal(false);
  public get showEditRuleDialog(): boolean { return this._showEditRuleDialog(); }
  public set showEditRuleDialog(value: boolean) { this._showEditRuleDialog.set(value); }
  public showRulesDialog: boolean = false;

  private readonly _newRuleKeyPattern = signal('');
  public get newRuleKeyPattern(): string { return this._newRuleKeyPattern(); }
  public set newRuleKeyPattern(value: string) { this._newRuleKeyPattern.set(value); }
  private readonly _newRuleMaxAgeSeconds = signal<number | null>(null);
  public get newRuleMaxAgeSeconds(): number | null { return this._newRuleMaxAgeSeconds(); }
  public set newRuleMaxAgeSeconds(value: number | null) { this._newRuleMaxAgeSeconds.set(value); }
  private readonly _newRuleTimeUnit = signal(1);
  public get newRuleTimeUnit(): number { return this._newRuleTimeUnit(); }
  public set newRuleTimeUnit(value: number) { this._newRuleTimeUnit.set(value); }
  public editRuleTimeUnit: number = 1;
  public readonly exportConfig: XLSXExportConfig[] = [
    { fieldName: 'username' },
    { fieldName: 'key' },
    { fieldName: 'value' },
    { fieldName: 'type' }
  ];
  public timeUnitOptions = [
    { label: 'Sekunden', value: 1 },
    { label: 'Minuten', value: 60 },
    { label: 'Stunden', value: 3600 },
    { label: 'Tage', value: 86400 }
  ];

  private subs: Subscription[] = [];

  constructor(
    private adminService: ProjectKeyvalAdminService,
    private projectService: ProjectService,
    private socketService: SocketAdminService,
    private confirmationService: ConfirmationService,
    private xlsxService: XlsxService,
    private toastr: ToastrService
  ) { }

  private get projectId(): string {
    return this.projectService.selectedProject?._id ?? '-';
  }

  public async ngOnInit() {
    await this.loadData();

    this.subs.push(
      this.socketService.onProjectKeyValChange.subscribe((entry) => {
        const strEntry = { ...entry, value: String(entry.value) } as ProjectKeyValEntry<string>;
        if (entry.projectId != this.projectService.selectedProject?._id) {
          return;
        }
        const exists = this.keyvals.some(kv => kv._id === entry._id);
        this.keyvals = exists
          ? this.keyvals.map(kv => kv._id === entry._id ? strEntry : kv)
          : [...this.keyvals, strEntry];
        this.filterKeyvals();
      }),
      this.socketService.onProjectKeyValClean.subscribe(() => {
        void this.loadData();
      }),
      this.socketService.onProjectKeyValDelete.subscribe(() => {
        this.keyvals = [];
        this.filterKeyvals();
      })
    );
  }

  public ngOnDestroy() {
    this.subs.forEach(s => s.unsubscribe());
  }

  private async loadData() {
    const [keyvals, rules, users] = await Promise.all([
      this.adminService.getProjectKeyVals(this.projectId),
      this.adminService.getMaxAgeRules(this.projectId),
      this.adminService.getProjectKeyValUsers(this.projectId)
    ]);
    this.keyvals = keyvals.map(kv => ({ ...kv, value: String(kv.value) }));
    this.maxAgeRules = rules;
    this.userMap = users;
    this.filterKeyvals();
  }

  public getUserName(userId: string): string {
    if (!userId) return '';
    const user = this.userMap[userId];
    if (user?.name) return user.name;
    if (user?.guestId) return user.guestId;
    return userId;
  }

  public isSystemEntry(type: string): boolean {
    return isSystemKeyValType(type);
  }

  public get systemEntryCount(): number {
    return this.keyvals.filter(k => isSystemKeyValType(k.type)).length;
  }

  public get systemEntriesToggleLabel(): string {
    return this.showSystemEntries
      ? $localize`:@@hide-system-entries:Systemeinträge ausblenden`
      : $localize`:@@show-system-entries:Systemeinträge anzeigen`;
  }

  public toggleSystemEntries(): void {
    this.showSystemEntries = !this.showSystemEntries;
    this.filterKeyvals();
  }

  public filterKeyvals() {
    const searchStrings = this.filterString.split(" ").map(s => s.toLowerCase().trim()).filter(s => s !== "");
    this.filteredKeyVals = this.keyvals.filter(k => {
      if (!this.showSystemEntries && isSystemKeyValType(k.type)) return false;
      return searchStrings.every(s => {
        if (k.key.toLowerCase().includes(s)) return true;
        if (k.type.toLowerCase().includes(s)) return true;
        if (k.value.toLowerCase().includes(s)) return true;
        return false;
      });
    });
  }

  public exportKeyvals() {
    try {
      const data = this.filteredKeyVals.map(keyval => ({
        username: this.getUserName(keyval.userId),
        key: keyval.key,
        value: keyval.value,
        type: keyval.type
      }));
      this.xlsxService.exportXlsx(data, this.exportConfig, 'datenspeicher');
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      this.toastr.error('ERROR', message);
    }
  }

  public startEdit(entry: ProjectKeyValEntry<string>) {
    this.editingEntry = entry;
    this.editValue = entry.value;
    this.showEditKeyValDialog = true;
  }

  public cancelEdit() {
    this.showEditKeyValDialog = false;
    this.editingEntry = null;
    this.editValue = "";
  }

  public async saveEdit() {
    if (!this.editingEntry) return;
    const editedId = this.editingEntry._id;
    const newValue = this.editValue.trim();
    await this.adminService.updateProjectKeyVal(this.projectId, editedId, newValue);
    this.keyvals = this.keyvals.map(kv => kv._id === editedId ? { ...kv, value: newValue } : kv);
    this.filterKeyvals();
    this.showEditKeyValDialog = false;
    this.editingEntry = null;
    this.editValue = "";
  }

  public async deleteEntry(entry: ProjectKeyValEntry<string>) {
    await this.adminService.deleteProjectKeyVal(entry.userId, this.projectId, entry.key, entry.type);
    this.keyvals = this.keyvals.filter(kv => kv._id !== entry._id);
    this.filterKeyvals();
  }

  public openKeyvalMenu(event: Event, entry: ProjectKeyValEntry<string>) {
    this.activeKeyvalMenuItems = [
      {
        label: $localize`:@@edit:Bearbeiten`,
        icon: 'ri-pencil-line',
        command: () => this.startEdit(entry)
      },
      {
        label: $localize`:@@delete-keyval:KeyVal löschen`,
        icon: 'ri-delete-bin-line',
        command: () => this.confirmDeleteEntry(entry)
      }
    ];
    this.keyvalMenu.toggle(event);
  }

  private confirmDeleteEntry(entry: ProjectKeyValEntry<string>) {
    this.confirmationService.confirm({
      header: $localize`:@@delete-keyval-confirmation:KeyVal löschen?`,
      message: $localize`:@@delete-keyval-message:Möchtest du diesen KeyVal-Eintrag wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`,
      icon: 'ri-error-warning-line',
      rejectButtonProps: {
        label: $localize`:@@delete-reject:Abbrechen`,
        severity: 'secondary',
        outlined: true
      },
      acceptButtonProps: {
        label: $localize`:@@delete-accept:Löschen`,
        severity: 'danger'
      },
      accept: () => {
        void this.deleteEntry(entry);
      },
      reject: () => {
        this.confirmationService.close();
      }
    });
  }

  public async deleteAll() {
    await this.adminService.deleteAllProjectKeyVals(this.projectId);
    this.keyvals = [];
    this.filterKeyvals();
  }

  public confirmDeleteAll() {
    this.confirmationService.confirm({
      header: $localize`:@@delete-all-keyvals-confirmation:Alle KeyVals löschen?`,
      message: $localize`:@@delete-all-keyvals-message:Möchtest du wirklich alle KeyVal-Einträge in diesem Projekt löschen? Systemeinträge (z. B. Gruppenchat-Nachrichten) bleiben dabei erhalten. Diese Aktion kann nicht rückgängig gemacht werden.`,
      icon: 'ri-error-warning-line',
      rejectButtonProps: {
        label: $localize`:@@delete-reject:Abbrechen`,
        severity: 'secondary',
        outlined: true
      },
      acceptButtonProps: {
        label: $localize`:@@delete-all-keyvals-accept:Alle löschen`,
        severity: 'danger'
      },
      accept: () => {
        void this.deleteAll();
      },
      reject: () => {
        this.confirmationService.close();
      }
    });
  }

  // MaxAge Rules
  public async addRule() {
    if (!this.newRuleKeyPattern || !this.newRuleMaxAgeSeconds) return;
    const seconds = this.newRuleMaxAgeSeconds * this.newRuleTimeUnit;
    const rule = await this.adminService.addMaxAgeRule(this.projectId, this.newRuleKeyPattern, seconds);
    this.maxAgeRules = [...this.maxAgeRules, rule];
    this.newRuleKeyPattern = "";
    this.newRuleMaxAgeSeconds = null;
    this.newRuleTimeUnit = 1;
  }

  public startEditRule(rule: ProjectKeyValMaxAgeRule) {
    this.editingRule = rule;
    this.editRuleKeyPattern = rule.keyPattern;
    const bestUnit = this.detectBestUnit(rule.maxAgeSeconds);
    this.editRuleTimeUnit = bestUnit;
    this.editRuleMaxAgeSeconds = rule.maxAgeSeconds / bestUnit;
    this.showEditRuleDialog = true;
  }

  public formatMaxAge(seconds: number): string {
    const unit = this.detectBestUnit(seconds);
    const value = seconds / unit;
    const label = this.timeUnitOptions.find(o => o.value === unit)?.label ?? 'Sekunden';
    return `${value.toString()} ${label}`;
  }

  private detectBestUnit(seconds: number): number {
    if (seconds % 86400 === 0) return 86400;
    if (seconds % 3600 === 0) return 3600;
    if (seconds % 60 === 0) return 60;
    return 1;
  }

  public cancelEditRule() {
    this.showEditRuleDialog = false;
    this.editingRule = null;
  }

  public async saveEditRule() {
    if (!this.editingRule) return;
    const ruleId = this.editingRule._id;
    const keyPattern = this.editRuleKeyPattern;
    const seconds = this.editRuleMaxAgeSeconds * this.editRuleTimeUnit;
    await this.adminService.updateMaxAgeRule(this.projectId, ruleId, keyPattern, seconds);
    this.maxAgeRules = this.maxAgeRules.map(r => r._id === ruleId ? { ...r, keyPattern, maxAgeSeconds: seconds } : r);
    this.showEditRuleDialog = false;
    this.editingRule = null;
  }

  public async deleteRule(rule: ProjectKeyValMaxAgeRule) {
    await this.adminService.removeMaxAgeRule(this.projectId, rule._id);
    this.maxAgeRules = this.maxAgeRules.filter(r => r._id !== rule._id);
  }

  public getRuleMenuItems(rule: ProjectKeyValMaxAgeRule): MenuItem[] {
    return [
      {
        label: $localize`:@@edit:Bearbeiten`,
        icon: 'ri-pencil-line',
        command: () => this.startEditRule(rule)
      },
      {
        label: $localize`:@@delete-rule:Regel löschen`,
        icon: 'ri-delete-bin-line',
        command: () => this.confirmDeleteRule(rule)
      }
    ];
  }

  private confirmDeleteRule(rule: ProjectKeyValMaxAgeRule) {
    this.confirmationService.confirm({
      header: $localize`:@@delete-rule-confirmation:Regel löschen?`,
      message: $localize`:@@delete-rule-message:Möchtest du diese Regel wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`,
      icon: 'ri-error-warning-line',
      rejectButtonProps: {
        label: $localize`:@@delete-reject:Abbrechen`,
        severity: 'secondary',
        outlined: true
      },
      acceptButtonProps: {
        label: $localize`:@@delete-accept:Löschen`,
        severity: 'danger'
      },
      accept: () => {
        void this.deleteRule(rule);
      },
      reject: () => {
        this.confirmationService.close();
      }
    });
  }
}
